import React, { useEffect, useState } from 'react';
import './FilterSelect.css';
import { transformCommasToPipes } from '../functions/transformCommasToPipes';
import { transformPipesToCommas } from '../functions/transformPipesToCommas';

interface FilterTextProps {
  name: string;
  label: string;
  value?: string;
  onChange?: (name: string, value: any) => void;
}

export const FilterText: React.FC<FilterTextProps> = ({
  name,
  label,
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState<string>('');

  const handleClear = () => {
    setInputValue('');
    onChange?.(name, '');
  };

  // Handler for when input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (newValue === '') {
      handleClear();
    } else {
      setInputValue(newValue);
    }
  };

  // Debounce effect for input changes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue !== '') {
        const transformedValue = transformCommasToPipes(inputValue);
        onChange && onChange(name, transformedValue);
      }
    }, 1200);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (value !== undefined) {
      // Extract the actual value from formatted filter strings like "ARTNO.like(%10404784%)"
      let displayValue = value;

      // Check if value is in the format field.operator(value)
      const operatorMatch = value.match(
        /^[^.]+\.(eq|gt|gte|lt|lte|like)\((.+)\)$/
      );
      if (operatorMatch) {
        displayValue = operatorMatch[2]; // Extract the value inside parentheses

        // Remove percentage signs if present (for LIKE operator)
        if (displayValue.startsWith('%') && displayValue.endsWith('%')) {
          // Remove first and last character (the % signs)
          displayValue = displayValue.substring(1, displayValue.length - 1);
        }
      }

      // If the value has pipes, convert them back to commas for display
      displayValue = transformPipesToCommas(displayValue);
      setInputValue(displayValue);
    }
  }, [value, name]);

  return (
    <div>
      <label className="slicer-label">{label}</label>
      <div className="filter-text-container">
        <input
          type="text"
          className="filter-text-input"
          name={name}
          value={inputValue}
          onChange={handleChange}
          placeholder="Enter value or comma-separated values"
        />
        {inputValue && (
          <button
            type="button"
            className="filter-text-clear"
            onClick={handleClear}
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};
